"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";

import { affiliateConfig } from "@/config/affiliate";
import pushGAFooter from "@/libs/ga4/action/footer";
import { convertToKebabCase } from "@/utils/common/convertHelper/convertToKebabCase";
import { cn } from "@/utils/common/styleHelper/utils";
import { FeaturesTypePathEnum } from "@/utils/enums/FeaturePathGlobalEnum";

import type {
  AboutGotherListInterface,
  FollowUsListInterface,
  OtherListInterface,
  ProductListInterface,
} from "./TemplateInterface";

type FooterContentProps = {
  productsList: ProductListInterface[];
  othersList: OtherListInterface[];
  followsUsMapping: FollowUsListInterface[];
  downloadAppsMapping: FollowUsListInterface[];
  aboutListFilter: AboutGotherListInterface[];
  footerLangTexts: object;
  lang: string;
};

const FooterContent: React.FC<FooterContentProps> = (props) => {
  const queryString = useSearchParams();
  const pathname = usePathname();

  const {
    productsList,
    othersList,
    followsUsMapping,
    aboutListFilter,
    footerLangTexts,
    lang,
    downloadAppsMapping,
  } = props;

  const t = (key: string) => {
    return (footerLangTexts as any)[key];
  };

  const handleClickProductsList = (name: string) => {
    pushGAFooter()?.ClickMenu(`product_${name}`);
  };

  const handleClickOther = (name: string) => {
    pushGAFooter()?.ClickMenu(`other_${name}`);
  };

  const handleClickAbout = (name: string) => {
    pushGAFooter()?.ClickMenu(`about_${name}`);
  };

  const handleClickDownloadApp = (name: string) => {
    pushGAFooter()?.ClickDownloadApp(name);
  };

  const handleClickSocial = (name: string) => {
    pushGAFooter()?.ClickSocial(name);
  };

  const handleClickPartnership = () => {
    pushGAFooter()?.ClickPartnership();
  };

  if (
    pathname &&
    pathname.includes(`/${FeaturesTypePathEnum.FLIGHTS}/booking`) &&
    !pathname.includes(`/${FeaturesTypePathEnum.FLIGHTS}/booking/success`)
  ) {
    return <div />;
  }

  if (
    pathname &&
    pathname.includes(`/${FeaturesTypePathEnum.HOTELS}/booking`) &&
    !pathname.includes(`/${FeaturesTypePathEnum.HOTELS}/booking/success`)
  ) {
    return <div />;
  }

  if (
    pathname &&
    pathname.includes(`/${FeaturesTypePathEnum.EXPERIENCES}/booking`) &&
    !pathname.includes(`/${FeaturesTypePathEnum.EXPERIENCES}/booking/success`)
  ) {
    return <div />;
  }

  if (
    pathname &&
    pathname.includes(`/${FeaturesTypePathEnum.TOURS}/booking`) &&
    !pathname.includes(`/${FeaturesTypePathEnum.TOURS}/booking/success`)
  ) {
    return <div />;
  }

  if (
    pathname &&
    pathname.includes(`/${FeaturesTypePathEnum.CARRENTALS}/booking`) &&
    !pathname.includes(`${FeaturesTypePathEnum.CARRENTALS}/booking/success`)
  ) {
    return <div />;
  }

  if (queryString && queryString.get("summary")) return <div />;

  if (pathname && pathname.includes("/promotions/register-campaign")) {
    return <div />;
  }
  if (pathname && pathname.includes("/register")) {
    return <div />;
  }

  const isNewsRoom = !!(pathname && pathname.includes("/news-room/"));

  return (
    <div id={convertToKebabCase("FooterContent")} className="footer-layout">
      <div
        className={cn("footer border-t bg-background-baigeBG", {
          "mb-14 md:mb-0": isNewsRoom,
        })}
      >
        <div className="mx-auto max-w-[1200px] py-[24px] md:pb-0 md:pt-[24px] lg:pt-[40px]">
          <div className="md:pb-[24px]">
            <section className="grid gap-6 md:grid-cols-75/25 md:gap-4">
              <section className="grid md:grid-cols-3">
                <div className="hidden gap-6 md:grid">
                  <div className="interest">
                    <div className="link-interest flex flex-col gap-2">
                      <p className="title-interest text-xl font-bold">
                        {t("services")}
                      </p>
                      {productsList.map((app, index) => (
                        <p key={index}>
                          <button
                            type="button"
                            onClick={() => handleClickProductsList(app.label)}
                          >
                            <Link
                              key={index}
                              href={app.link}
                              className="text-text-sub no-underline md:text-base"
                              title={app.productNameMap}
                            >
                              {app.productNameMap}
                            </Link>
                          </button>
                        </p>
                      ))}
                    </div>
                  </div>
                  <div className="others">
                    <div className="link-interest flex flex-col gap-2">
                      <p className="title-interest text-xl font-bold">
                        {t("others")}
                      </p>
                      {othersList.map((app, index) => {
                        if (app.productNameMap === "affiliatePartner") {
                          if (affiliateConfig.hideAffiliateInFooter) {
                            return null;
                          }
                          return (
                            <a
                              className="cursor-pointer text-text-sub no-underline"
                              key={index}
                              title={app.label}
                              href={app.link}
                              target="_blank"
                            >
                              {app.label}
                            </a>
                          );
                        }
                        return (
                          <p key={index}>
                            <button
                              type="button"
                              onClick={() =>
                                handleClickOther(app.productNameMap)
                              }
                            >
                              <Link
                                key={index}
                                href={app.link}
                                className="text-text-sub no-underline"
                                title={app.label}
                              >
                                {app.label}
                              </Link>
                            </button>
                          </p>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div className="hidden md:block">
                  <div className="for-customer">
                    <div className="link-about flex flex-col gap-2">
                      <p className="title-about text-xl font-bold">
                        {t("aboutGother")}
                      </p>
                      {aboutListFilter.map((app, index) => (
                        <p key={index}>
                          <button
                            type="button"
                            onClick={() => handleClickAbout(app.label)}
                          >
                            <Link
                              key={index}
                              href={app.link}
                              className=" text-text-sub no-underline"
                              title={app.langKeyMap}
                            >
                              {app.langKeyMap}
                            </Link>
                          </button>
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-center gap-6 md:items-start">
                  <div className="block">
                    <div className="download flex w-full flex-col gap-2">
                      <p className="download-title hidden text-xl font-bold md:block">
                        {t("downloadApplications")}
                      </p>
                      <div className="Image-store flex justify-center gap-2 md:justify-between">
                        {downloadAppsMapping.map((app, index) => (
                          <button
                            type="button"
                            onClick={() => handleClickDownloadApp(app.label)}
                            key={index}
                          >
                            <Link
                              title={app.alt}
                              href={`${app.link}`}
                              target="_blank"
                            >
                              <Image
                                className="z-1 h-10 w-[135px]"
                                width={100}
                                height={100}
                                src={app.image}
                                alt={app.alt}
                              />
                            </Link>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="follow flex flex-col gap-2">
                    <p className="follow-title hidden text-xl font-bold md:block">
                      {t("followUs")}
                    </p>
                    <div className="follow-Image flex">
                      {followsUsMapping.map((app, index) => (
                        <button
                          type="button"
                          key={index}
                          onClick={() => handleClickSocial(app.label)}
                          className="mr-4 p-[0.063rem]"
                        >
                          <Link href={app.link} title={app.alt}>
                            <div className="size-[30px] rounded-none">
                              <Image
                                width={30}
                                height={30}
                                sizes="100vw"
                                style={{ width: "100%", height: "auto" }}
                                quality={100}
                                src={app.image}
                                alt={app.alt}
                              />
                            </div>
                          </Link>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="hidden w-full md:flex">
                    <button
                      type="button"
                      onClick={() => handleClickPartnership()}
                      className="mr-4 w-full p-[0.063rem]"
                    >
                      <Link href={`/${lang}/partnership`} title={t("connect")}>
                        <div className="informative hidden min-h-10 w-full flex-row justify-center gap-2 rounded-lg border border-neutral-400 bg-white px-3 py-2 md:flex">
                          <Image
                            className="z-1 size-6"
                            width={100}
                            height={100}
                            src="https://d31jk6ljx7wihh.cloudfront.net/icons/nav/helpCenter/tos-icon-header.svg"
                            alt="partnership-icon"
                          />
                          <div className="label font-semibold text-text-base lg:whitespace-nowrap">
                            {t("connect")}
                          </div>
                        </div>
                      </Link>
                    </button>
                  </div>
                </div>
              </section>
              <section className="">
                <div className="row4 mt-6 hidden flex-col gap-6 md:mt-0 md:flex">
                  <div className="rating-review flex flex-col gap-2">
                    <p className="payment-title text-xl font-bold">
                      {t("RatingsAndReviewsPoweredBy")}
                    </p>
                    <div className="tripadvisor-icon">
                      <Image
                        className="z-1 h-8 w-[10.75rem]"
                        width={172}
                        height={32}
                        src="https://d31jk6ljx7wihh.cloudfront.net/images/tripadvisor-image.svg"
                        alt="tripadvisor"
                      />
                    </div>
                  </div>
                  <div className="copperate-info flex flex-col gap-2">
                    <p className="info-title text-xl font-bold">
                      {t("accreditedMember")}
                    </p>
                    <div className="info-detail text-text-sub">
                      <p className="subContentSerif text-sm text-text-sub">
                        IATA BSP No. 35308932
                      </p>
                      {/* <p className="subContentSerif text-text-sub">
                    TG E-Ticket Direct No. 35202473
                  </p> */}

                      <p className="subContentSerif text-sm text-text-sub">
                        Company Registration/TAX ID: 0105552065599
                      </p>
                    </div>
                  </div>
                  <div className="Copyright flex  gap-4 text-center lg:mr-4 xl:mr-0">
                    <div className="geotrust-Image">
                      <Image
                        className="z-1 h-[2.375rem]"
                        width={79}
                        height={38}
                        src="https://d31jk6ljx7wihh.cloudfront.net/images/copyrightImages/geotrust-icon.svg"
                        alt="geotrust"
                      />
                    </div>
                    <div className="dss-Image">
                      <Image
                        className="z-1 h-[2.375rem]"
                        width={95}
                        height={38}
                        src="https://d31jk6ljx7wihh.cloudfront.net/images/copyrightImages/dss-icon.svg"
                        alt="dss"
                      />
                    </div>
                    <div className="iata-Image">
                      <Image
                        className="z-1 h-[2.375rem] w-[4.063rem]"
                        width={95}
                        height={47}
                        src="https://d31jk6ljx7wihh.cloudfront.net/images/copyrightImages/iata-icon.svg"
                        alt="iata"
                      />
                    </div>
                  </div>
                </div>
              </section>
            </section>
          </div>
          <div className="Copyright flex flex-col gap-6 border-t border-stroke-light pt-6 text-center md:py-6">
            <div className="max-w-div-copyright-Image">
              <p className="text-xs text-black md:text-base ">
                {t("copyright1")}
              </p>
              <p className="whitespace-break-spaces text-xs text-black md:text-base">
                {t("copyright2")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FooterContent;
